import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { fetchProducts } from "../lib/api";

// Map product IDs to detail-page routes. Bronze gets its own page even though
// it isn't purchasable yet.
const DETAIL_ROUTES = {
  ivory: "/ivory",
  bronze: "/bronze",
  "qzip-black": "/qzip",
};

// Optional shop-tile image override (useful when the detail-page image has a
// white studio background we don't want on the shop grid).
const SHOP_IMAGE_OVERRIDES = {
  "qzip-black": "/qzip-white.jpg",
};

const ProductCard = ({ p, idx }) => {
  const detailHref = DETAIL_ROUTES[p.id];
  const isClickable = Boolean(detailHref);
  const tileImage = SHOP_IMAGE_OVERRIDES[p.id] || p.image;

  const Media = (
    <div className="relative aspect-[3/4] bg-black border hairline overflow-hidden grain-overlay">
      {p.image ? (
        <img
          src={tileImage}
          alt={p.name}
          className={`absolute inset-0 w-full h-full object-contain transition-transform duration-700 ${
            isClickable ? "group-hover:scale-105" : ""
          } ${p.id === "bronze" ? "scale-125" : ""} ${p.category === "apparel" ? "scale-110" : ""}`}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-center px-6">
          <div>
            <p className="text-[10px] uppercase tracking-label gold-text">In the lab</p>
            <p className="mt-5 font-serif text-3xl">{p.name}</p>
            <div className="divider-gold mx-auto my-6" />
            <p className="text-[10px] uppercase tracking-label text-[var(--secondary-text)]">
              {p.tagline || "Coming Soon"}
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const Caption = (
    <>
      <div className="mt-6">
        <h3 className="font-serif text-2xl">{p.name}</h3>
      </div>
      {p.available ? (
        <span className="mt-3 inline-flex items-center gap-2 text-[11px] uppercase tracking-label gold-text">
          Shop <ArrowRight size={12} />
        </span>
      ) : isClickable ? (
        <div className="mt-3 flex items-center justify-between">
          <p className="text-[11px] uppercase tracking-label gold-text">
            {p.tagline || "Coming Soon"}
          </p>
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-label gold-text">
            View <ArrowRight size={12} />
          </span>
        </div>
      ) : (
        <p className="mt-3 text-[11px] uppercase tracking-label gold-text">
          {p.tagline || "Coming Soon"}
        </p>
      )}
    </>
  );

  return (
    <motion.div
      key={p.id}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.9, delay: idx * 0.1 }}
      data-testid={`shop-card-${p.id}`}
      className="group"
    >
      {isClickable ? (
        <Link to={detailHref} data-testid={`shop-link-${p.id}`}>
          {Media}
          {Caption}
        </Link>
      ) : (
        <div data-testid={`shop-soon-${p.id}`}>
          {Media}
          {Caption}
        </div>
      )}
    </motion.div>
  );
};

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProducts = async () => {
    setLoading(true);
    setError(null);
    const delays = [0, 400, 1000, 2000];
    let lastErr = null;
    for (const delay of delays) {
      if (delay) await new Promise((r) => setTimeout(r, delay));
      try {
        const data = await fetchProducts();
        setProducts(data);
        setLoading(false);
        return;
      } catch (e) {
        lastErr = e;
      }
    }
    setError(lastErr?.message || "Network error");
    setLoading(false);
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const { men, women, apparel } = useMemo(() => {
    const m = products.filter((p) => (p.category || "men") === "men");
    const w = products.filter((p) => p.category === "women");
    const a = products.filter((p) => p.category === "apparel");
    return { men: m, women: w, apparel: a };
  }, [products]);

  return (
    <div data-testid="shop-page" className="pt-32 pb-24">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0 }}
          className="max-w-2xl"
        >
          <span className="text-[10px] uppercase tracking-label gold-text">
            Summit Scents · Shop
          </span>
          <h1 className="mt-5 font-serif text-5xl md:text-7xl leading-[1.0]">
            The Summit Collection.
          </h1>
          <div className="divider-gold mt-8" />
          <p className="mt-8 text-[var(--secondary-text)] leading-loose">
            Our collection, made for you.
          </p>
        </motion.div>

        {error && (
          <div className="mt-12 border hairline p-6 max-w-xl" data-testid="shop-error">
            <p className="text-[10px] uppercase tracking-label gold-text">Connection issue</p>
            <p className="mt-2 text-sm text-[var(--primary-text)] leading-relaxed">
              We couldn't reach our collection just now. This is usually a brief hiccup — please try again.
            </p>
            <button
              onClick={loadProducts}
              className="btn-secondary mt-5 text-xs"
              data-testid="shop-retry-btn"
            >
              Retry
            </button>
          </div>
        )}

        {/* ===== For Men ===== */}
        <div className="mt-20" data-testid="shop-section-men">
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight gold-text">For Men</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {men.map((p, idx) => (
              <ProductCard key={p.id} p={p} idx={idx} />
            ))}
          </div>
        </div>

        {/* ===== Gold Divider ===== */}
        <div className="mt-24 h-px bg-gradient-to-r from-transparent via-[var(--accent-gold)] to-transparent" aria-hidden />

        {/* ===== For Women ===== */}
        <div className="mt-20" data-testid="shop-section-women">
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight gold-text">For Women</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {women.length === 0 ? (
              <div className="col-span-full border hairline p-12 text-center" data-testid="women-empty">
                <p className="text-[10px] uppercase tracking-label gold-text">In the lab</p>
                <p className="mt-4 font-serif text-2xl">A composition for her</p>
                <p className="mt-3 text-sm text-[var(--secondary-text)]">
                  Coming soon.
                </p>
              </div>
            ) : (
              women.map((p, idx) => <ProductCard key={p.id} p={p} idx={idx} />)
            )}
          </div>
        </div>

        {/* ===== Gold Divider ===== */}
        {apparel.length > 0 && (
          <div className="mt-24 h-px bg-gradient-to-r from-transparent via-[var(--accent-gold)] to-transparent" aria-hidden />
        )}

        {/* ===== Apparel ===== */}
        {apparel.length > 0 && (
          <div className="mt-20" data-testid="shop-section-apparel">
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight gold-text">Apparel</h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {apparel.map((p, idx) => <ProductCard key={p.id} p={p} idx={idx} />)}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default Shop;
